/**
 * Plan ahead — design/PROTOTYPE-SPEC.md §13, the third Targets tab.
 *
 * Pick a month, build treatment packages, see what the month is worth if the
 * plan happens. Copy a package into another month instead of rebuilding it, and
 * send one to This month when it stops being a plan and starts being work.
 *
 * ## The numbers are the reason this exists
 *
 * A list of intentions is a note. What makes it planning is costing it: revenue,
 * stock cost, profit and margin per package and for the month. The arithmetic is
 * in `packages/core` and unit-tested — this file only renders it.
 *
 * ## The AI marketing assistant is not here
 *
 * The spec pairs this tab with one, generating social posts and emails. It has a
 * model and a costing (`docs/AI-ASSISTANT.md`) but **no per-account spend cap**,
 * so nothing bounds what it could cost on a £45/month plan. That is a decision,
 * not an engineering gap.
 */
import {
  formatMonth,
  monthPlanTotals,
  plannedMarginPercent,
  plannedRevenuePence,
  shiftMonth,
  type Plan as CorePlan,
} from '@inventory-tracker/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  copyPackage,
  createPackage,
  deletePackage,
  getPackages,
  listProducts,
  sendPackageToThisMonth,
  updatePackage,
  type PackageInputBody,
  type ProductRow,
  type TreatmentPackage,
} from '../lib/api.js';
import { c, mono, primaryButton, sans } from '../theme.js';
import { cardStyle, money, smallButton } from './format.js';

interface Props {
  /** The account's current month, `YYYY-MM`, from the server's clock. */
  currentMonth: string;
  plan: CorePlan;
  /** Tells Targets to refetch — sending a package changes This month. */
  onChanged: () => void;
}

export function PlanAhead({ currentMonth, plan, onChanged }: Props) {
  const [month, setMonth] = useState(currentMonth);
  const [packages, setPackages] = useState<TreatmentPackage[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async (m: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPackages(m);
      setPackages(res.packages);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(month);
  }, [load, month]);

  useEffect(() => {
    // The product list is only needed to build a recipe; a failure here should
    // not take the page down with it.
    void listProducts()
      .then((r) => setProducts(r.products ?? []))
      .catch(() => undefined);
  }, []);

  /** Costed shape the core functions expect. */
  const costed = useMemo(
    () =>
      packages.map((p) => ({
        ...p,
        items: p.items.map((i) => ({
          productId: i.productId,
          qty: i.qty,
          unitCostPence: i.unitCostPence,
        })),
      })),
    [packages],
  );

  const totals = useMemo(() => monthPlanTotals(costed), [costed]);

  async function mutate(fn: () => Promise<unknown>, alsoRefreshTargets = false) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load(month);
      if (alsoRefreshTargets) onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  const isPast = month < currentMonth;

  return (
    <div>
      {/* ------------------------------------------------------- month picker */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => setMonth(shiftMonth(month, -1))}
              style={{ ...smallButton, height: 38, width: 38, padding: 0 }}
              aria-label="Previous month"
            >
              ‹
            </button>
            <div style={{ minWidth: 168, textAlign: 'center' }}>
              <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 22, fontWeight: 300 }}>{formatMonth(month)}</div>
              {month === currentMonth && (
                <div style={{ fontSize: 11, color: c.green, letterSpacing: 0.6 }}>THIS MONTH</div>
              )}
              {isPast && (
                <div style={{ fontSize: 11, color: c.faint, letterSpacing: 0.6 }}>PAST</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setMonth(shiftMonth(month, 1))}
              style={{ ...smallButton, height: 38, width: 38, padding: 0 }}
              aria-label="Next month"
            >
              ›
            </button>
            {month !== currentMonth && (
              <button
                type="button"
                onClick={() => setMonth(currentMonth)}
                style={{ ...smallButton, height: 38 }}
              >
                Today
              </button>
            )}
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void mutate(async () => {
                const { id } = await createPackage({
                  name: 'New package',
                  pricePence: 0,
                  plannedCount: 1,
                  month,
                  items: [],
                });
                setEditing(id);
              })
            }
            style={{ ...primaryButton, width: 'auto', height: 42, padding: '0 18px' }}
          >
            Add a package
          </button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            color: c.danger,
            borderRadius: 12,
            padding: '11px 14px',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ------------------------------------------------------------ totals */}
      {packages.length > 0 && (
        <div
          style={{
            ...cardStyle,
            marginBottom: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 14,
          }}
        >
          <Stat label="Planned revenue" value={money(totals.revenuePence)} big />
          <Stat label="Stock cost" value={money(totals.costPence)} />
          <Stat
            label="Profit"
            value={money(totals.profitPence)}
            tone={totals.profitPence < 0 ? c.danger : undefined}
          />
          <Stat
            label="Margin"
            // Null rather than 0% when there is no revenue — see the core note.
            value={totals.marginPercent === null ? '—' : `${totals.marginPercent}%`}
            tone={
              totals.marginPercent !== null && totals.marginPercent < 0 ? c.danger : undefined
            }
          />
          <Stat label="Treatments" value={String(totals.treatments)} />
        </div>
      )}

      {/* ---------------------------------------------------------- packages */}
      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: c.muted2, fontSize: 13.5 }}>
          Loading…
        </div>
      ) : packages.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '32px 20px' }}>
          <p style={{ fontSize: 14, color: c.muted, margin: '0 0 6px' }}>
            Nothing planned for {formatMonth(month)}.
          </p>
          <p style={{ fontSize: 13, color: c.faint, margin: 0, lineHeight: 1.7 }}>
            Build a package: what it is made of, what you will charge and how many you plan.
            Then this month gets a number.
          </p>
        </div>
      ) : (
        packages.map((p) => (
          <PackageCard
            key={p.id}
            pkg={p}
            products={products}
            busy={busy}
            editing={editing === p.id}
            onEdit={() => setEditing(editing === p.id ? null : p.id)}
            onSave={(body) =>
              void mutate(async () => {
                await updatePackage(p.id, body);
                setEditing(null);
              })
            }
            onDelete={() => void mutate(() => deletePackage(p.id), true)}
            onCopy={(target) => void mutate(() => copyPackage(p.id, target))}
            onSend={() => void mutate(() => sendPackageToThisMonth(p.id), true)}
            month={month}
            plan={plan}
          />
        ))
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  big,
  tone,
}: {
  label: string;
  value: string;
  big?: boolean;
  tone?: string | undefined;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          textTransform: 'lowercase', fontFamily: mono,
          color: c.muted2,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: big ? 28 : 22, color: tone ?? c.ink }}>
        {value}
      </div>
    </div>
  );
}

function PackageCard({
  pkg,
  products,
  busy,
  editing,
  month,
  onEdit,
  onSave,
  onDelete,
  onCopy,
  onSend,
}: {
  pkg: TreatmentPackage;
  products: ProductRow[];
  busy: boolean;
  editing: boolean;
  month: string;
  plan: CorePlan;
  onEdit: () => void;
  onSave: (body: PackageInputBody) => void;
  onDelete: () => void;
  onCopy: (month: string) => void;
  onSend: () => void;
}) {
  const costedPkg = {
    pricePence: pkg.pricePence,
    plannedCount: pkg.plannedCount,
    items: pkg.items.map((i) => ({
      productId: i.productId,
      qty: i.qty,
      unitCostPence: i.unitCostPence,
    })),
  };
  const margin = plannedMarginPercent(costedPkg);

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      {editing ? (
        <PackageEditor
          pkg={pkg}
          products={products}
          busy={busy}
          month={month}
          onCancel={onEdit}
          onSave={onSave}
        />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 3 }}>
                <strong style={{ fontSize: 15.5 }}>{pkg.name}</strong>
                {pkg.sentToThisMonth && (
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 600,
                      letterSpacing: 0.6,
                      color: c.success,
                      border: `1px solid #D3DFCE`,
                      background: c.okBg,
                      borderRadius: 980,
                      padding: '2px 8px',
                    }}
                  >
                    IN THIS MONTH
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: c.muted }}>
                {money(pkg.pricePence)} × {pkg.plannedCount} ={' '}
                <strong style={{ color: c.ink }}>{money(plannedRevenuePence(costedPkg))}</strong>
                {margin !== null && (
                  <span style={{ color: margin < 0 ? c.danger : c.muted2 }}>
                    {' '}
                    · {margin}% margin
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={onEdit} disabled={busy} style={smallButton}>
                Edit
              </button>
              <button
                type="button"
                onClick={() => onCopy(shiftMonth(month, 1))}
                disabled={busy}
                style={smallButton}
                title={`Copy into ${formatMonth(shiftMonth(month, 1))}`}
              >
                Copy to next month
              </button>
              <button
                type="button"
                onClick={onSend}
                disabled={busy}
                style={pkg.sentToThisMonth ? smallButton : { ...primaryButton, width: 'auto', height: 36, padding: '0 14px' }}
              >
                {pkg.sentToThisMonth ? 'Take back' : 'Send to this month'}
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                style={{ ...smallButton, color: c.danger }}
              >
                Delete
              </button>
            </div>
          </div>

          {pkg.items.length > 0 && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: `1px solid ${c.border}`,
                fontSize: 13,
                color: c.muted,
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
              }}
            >
              {pkg.items.map((i) => (
                <span key={i.productId}>
                  {i.name} <span style={{ color: c.faint }}>× {i.qty}</span>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PackageEditor({
  pkg,
  products,
  busy,
  month,
  onCancel,
  onSave,
}: {
  pkg: TreatmentPackage;
  products: ProductRow[];
  busy: boolean;
  month: string;
  onCancel: () => void;
  onSave: (body: PackageInputBody) => void;
}) {
  const [name, setName] = useState(pkg.name);
  // Prices are held as strings so the field can be emptied mid-edit; converted
  // to integer pence only on save.
  const [price, setPrice] = useState(String(pkg.pricePence / 100));
  const [count, setCount] = useState(String(pkg.plannedCount));
  const [items, setItems] = useState(pkg.items.map((i) => ({ productId: i.productId, qty: i.qty })));

  const unused = products.filter((p) => !items.some((i) => i.productId === p.id));

  function save() {
    const pounds = Number(price);
    const n = Number(count);
    onSave({
      name: name.trim() || 'Untitled package',
      pricePence: Number.isFinite(pounds) ? Math.round(pounds * 100) : 0,
      plannedCount: Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0,
      month,
      items,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Field label="Name" value={name} onChange={setName} grow />
        <Field label="Price (£)" value={price} onChange={setPrice} width={120} />
        <Field label="Planned" value={count} onChange={setCount} width={100} />
      </div>

      <div>
        <div style={editorLabel}>What it uses</div>
        {items.length === 0 && (
          <p style={{ fontSize: 12.5, color: c.faint, margin: '0 0 8px' }}>
            Nothing yet. A package with no recipe still plans revenue; it just cannot be costed.
          </p>
        )}
        {items.map((item, idx) => {
          const product = products.find((p) => p.id === item.productId);
          return (
            <div
              key={item.productId}
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
            >
              <span style={{ flex: 1, fontSize: 13.5 }}>{product?.name ?? 'Unknown product'}</span>
              <input
                type="number"
                min={1}
                value={item.qty}
                onChange={(e) =>
                  setItems(
                    items.map((i, n) =>
                      n === idx ? { ...i, qty: Math.max(1, Number(e.target.value) || 1) } : i,
                    ),
                  )
                }
                style={{ ...inputStyle, width: 72, height: 36 }}
              />
              <button
                type="button"
                onClick={() => setItems(items.filter((_, n) => n !== idx))}
                style={{ ...smallButton, height: 36, color: c.danger }}
              >
                Remove
              </button>
            </div>
          );
        })}

        {unused.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) setItems([...items, { productId: e.target.value, qty: 1 }]);
            }}
            style={{ ...inputStyle, height: 38, marginTop: 4 }}
          >
            <option value="">Add a product…</option>
            {unused.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          style={{ ...primaryButton, width: 'auto', height: 40, padding: '0 18px' }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} style={{ ...smallButton, height: 40 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const editorLabel: React.CSSProperties = {
  fontSize: 14,
  color: c.label,
  fontWeight: 500,
  marginBottom: 7,
};

const inputStyle: React.CSSProperties = {
  padding: '0 11px',
  borderRadius: 12,
  border: `1px solid ${c.borderStrong}`,
  fontSize: 13.5,
  fontFamily: sans,
  background: c.white,
  boxSizing: 'border-box',
};

function Field({
  label,
  value,
  onChange,
  width,
  grow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width?: number;
  grow?: boolean;
}) {
  return (
    <div style={{ flex: grow ? 1 : undefined, minWidth: width ?? 160 }}>
      <div style={editorLabel}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, height: 40, width: '100%' }}
      />
    </div>
  );
}
